import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ONBOARDING_STEPS, type BranchRecordDto, type OnboardingDto, type OnboardingStep, type OrganizationDto, type OrganizationInput, type TreasuryRecordDto, type WarehouseRecordDto
} from "@delta/shared";
import { useLang, type T } from "../i18n";
import { useSession } from "../lib/session";
import { api, apiUpload, ApiFailure } from "../lib/api";
import { errorText, fieldErrorsOf } from "../lib/errors";
import { Icon } from "../components/Icon";
import { PageHead, Shell } from "../components/Shell";
import { useToast } from "../components/Toast";

// The onboarding wizard of a business that signed up. Each step saves through the usual settings APIs (with their
// permission checks and plan limits), so leaving and coming back resumes where the owner stopped. The accounting step
// only points to the accounting screens: Delta creates no account, tax type, opening balance or transaction.

export const entitlementText = (t: T, e: unknown) => (e instanceof ApiFailure && e.code === "entitlement_limit" ? t("entitlementLimit", { n: e.limit ?? "" }) : null);

export function OnboardingBanner() {
  const { t } = useLang();
  const { me, can } = useSession();
  if (!me?.organization.onboardingPending || !can("setting")) return null;
  return <div className="ob-banner" data-testid="onboarding-banner"><span><Icon name="info" /> {t("obBanner")}</span><Link className="btn btn-primary btn-sm" to="/onboarding">{t("obBannerGo")}</Link></div>;
}

export function Onboarding() {
  const { t, L } = useLang();
  const s = useSession();
  const nav = useNavigate();
  const [ob, setOb] = useState<OnboardingDto | null>(null);
  const [step, setStep] = useState<OnboardingStep | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const reload = useCallback(async (go?: boolean) => {
    try {
      const o = await api<OnboardingDto>("GET", "/onboarding");
      setOb(o);
      if (go) setStep(ONBOARDING_STEPS.find((k) => o.steps[k] === "todo") ?? "logo");
      return o;
    } catch (e) { setFailed(errorText(t, e)); return null; }
  }, [t]);
  useEffect(() => { void reload(true); }, [reload]);
  const canEdit = s.can("setting");

  const next = async () => {
    const o = await reload();
    if (!o || !step) return;
    const i = ONBOARDING_STEPS.indexOf(step);
    setStep(ONBOARDING_STEPS.slice(i + 1).find((k) => o.steps[k] === "todo") ?? ONBOARDING_STEPS[Math.min(i + 1, 5)]!);
  };

  let body = null;
  if (failed) body = <div className="state-box error"><Icon name="alert" /><h3>{failed}</h3></div>;
  else if (!ob || !step) body = <div className="state-box"><p>{t("loading")}</p></div>;
  else if (!canEdit) body = <div className="ob-body"><p>{t("obNotAdmin")}</p></div>;
  else body = <StepBody step={step} ob={ob} onDone={next} onFinish={async () => { await s.refresh(); nav("/home"); }} onBack={() => setStep(ONBOARDING_STEPS[Math.max(0, ONBOARDING_STEPS.indexOf(step) - 1)]!)} />;

  const e = ob?.entitlements;
  return (
    <Shell navKey="/onboarding">
      <PageHead crumbs={[[t("home"), "/home"], [t("obTitle")]]} title={t("obTitle")} sub={t("obLede")} />
      <div className="ob-wrap">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <section className="card"><ol className="ob-steps" data-testid="ob-steps">
            {ONBOARDING_STEPS.map((k, i) => {
              const st = ob?.steps[k] ?? "todo";
              return (
                <li key={k} className={st}><button className={step === k ? "on" : ""} onClick={() => setStep(k)} data-step={k} data-state={st}>
                  <span className="n">{st === "done" ? <Icon name="check" /> : i + 1}</span>{t(`obStep_${k}`)}<span className="st">{t(`obState_${st}`)}</span>
                </button></li>
              );
            })}
          </ol></section>
          {e && (
            <section className="card ob-plan" data-testid="ob-plan">
              <b>{t("obPlanTitle")}</b>
              <span>{e.plan ? `${L(e.plan)} · ${t("obPlanUntil", { d: e.plan.endsOn })}` : t("obPlanNone")}</span>
              <span className="hint">{t("obLimits", { u: e.limits.users, b: e.limits.branches, w: e.limits.warehouses, t: e.limits.treasuries, p: e.limits.posDevices })}</span>
              {!e.plan && <span className="hint">{t("obPlanNoneHint")}</span>}
            </section>
          )}
        </div>
        <section className="card" data-testid="ob-body">{body}</section>
      </div>
    </Shell>
  );
}

function StepBody({ step, ob, onDone, onBack, onFinish }: { step: OnboardingStep; ob: OnboardingDto; onDone(): Promise<void>; onBack(): void; onFinish(): Promise<void> }) {
  const { t } = useLang();
  const props = { ob, onDone };
  const inner = step === "organization" ? <OrgStep {...props} /> : step === "branch" ? <BranchStep {...props} /> : step === "warehouse" ? <WarehouseStep {...props} />
    : step === "treasury" ? <TreasuryStep {...props} /> : step === "accounting" ? <AccountingStep {...props} /> : <LogoStep {...props} onFinish={onFinish} />;
  return (
    <div className="ob-body" data-current={step}>
      <h2>{t(`obStep_${step}`)}</h2>
      {inner}
      {step !== "organization" && <div><button className="btn btn-ghost btn-sm" onClick={onBack}><Icon name="back" className="flip" />{t("obBack")}</button></div>}
    </div>
  );
}

interface StepProps { ob: OnboardingDto; onDone(): Promise<void> }

/** A small form helper: values, field errors from the server, an alert and a busy flag. */
function useForm<V extends Record<string, string>>(init: V) {
  const [v, setV] = useState<V>(init);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { t } = useLang();
  const input = (k: keyof V & string, label: string, o: { req?: boolean; full?: boolean; ltr?: boolean } = {}) => (
    <div className={`field ${o.full ? "full" : ""} ${errs[k] ? "invalid" : ""}`}>
      <label htmlFor={`ob-${k}`} className={o.req ? "req" : ""}>{label}</label>
      <input id={`ob-${k}`} className="control" dir={o.ltr ? "ltr" : undefined} value={v[k]} onChange={(e) => { setV((x) => ({ ...x, [k]: e.target.value })); setErrs((x) => ({ ...x, [k]: "" })); }} />
      <div className="ferr" role="alert">{errs[k]}</div>
    </div>
  );
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setAlert(null);
    try { await fn(); return true; } catch (e) {
      setErrs(fieldErrorsOf(t, e)); setAlert(entitlementText(t, e) ?? errorText(t, e)); return false;
    } finally { setBusy(false); }
  };
  return { v, setV, errs, setErrs, alert, busy, input, run };
}

const Alert = ({ text }: { text: string | null }) => (text ? <div className="dlg-alert show" role="alert"><Icon name="alert" /><span>{text}</span></div> : null);

function OrgStep({ onDone }: StepProps) {
  const { t } = useLang();
  const [org, setOrg] = useState<OrganizationDto | null>(null);
  const f = useForm({ nameEn: "", nameAr: "", commercialRecord: "", taxNumber: "", phone: "", email: "", country: "", city: "", neighborhood: "", street: "" });
  useEffect(() => {
    api<OrganizationDto>("GET", "/settings/organization").then((o) => {
      setOrg(o);
      f.setV({ nameEn: o.nameEn, nameAr: o.nameAr, commercialRecord: o.commercialRecord, taxNumber: o.taxNumber, phone: o.phone, email: o.email, country: o.country, city: o.city, neighborhood: o.neighborhood, street: o.street });
    }, () => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!org) return <p>{t("loading")}</p>;
  const save = async () => {
    const body: OrganizationInput = { ...f.v, note: org.note, landline: org.landline, fax: org.fax };
    if (await f.run(async () => { await api("PUT", "/settings/organization", body); await api("PUT", "/onboarding/steps/organization", { state: "done" }); })) await onDone();
  };
  return (
    <form noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <p className="hint">{t("obOrgHint")}</p>
      <div className="ob-form">
        {f.input("nameEn", t("nameEn"), { req: true })}{f.input("nameAr", t("nameAr"), { req: true })}
        {f.input("commercialRecord", t("commercialRecord"), { ltr: true })}{f.input("taxNumber", t("taxNumber"), { ltr: true })}
        {f.input("phone", t("phone"), { ltr: true })}{f.input("email", t("emailF"), { ltr: true })}
        {f.input("country", t("country"))}{f.input("city", t("city"))}{f.input("neighborhood", t("neighborhood"))}{f.input("street", t("street"))}
      </div>
      <Alert text={f.alert} />
      <div className="ob-actions"><button className="btn btn-primary" type="submit" disabled={f.busy}>{t("obSaveContinue")}</button></div>
    </form>
  );
}

function useList<X>(url: string) {
  const [rows, setRows] = useState<X[] | null>(null);
  useEffect(() => { api<X[]>("GET", url).then(setRows, () => setRows([])); }, [url]);
  return rows;
}

function BranchStep({ ob, onDone }: StepProps) {
  const { t, L } = useLang();
  const s = useSession();
  const rows = useList<BranchRecordDto>("/settings/branches");
  const f = useForm({ nameEn: "", nameAr: "", phone: "", addressEn: "", addressAr: "" });
  if (!rows) return <p>{t("loading")}</p>;
  const save = async () => {
    if (await f.run(() => api("POST", "/settings/branches", { ...f.v, monthlyExpense: 0, currency: "SAR" }))) { await s.refresh(); await onDone(); }
  };
  return ob.steps.branch === "done" ? <Existing names={rows.map((r) => L(r))} onDone={onDone} /> : (
    <form noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <p className="hint">{t("obBranchHint")}</p>
      <div className="ob-form">{f.input("nameEn", t("nameEn"), { req: true })}{f.input("nameAr", t("nameAr"), { req: true })}
        {f.input("phone", t("phone"), { ltr: true })}{f.input("addressEn", t("addressEn"))}{f.input("addressAr", t("addressAr"), { full: true })}</div>
      <Alert text={f.alert} />
      <div className="ob-actions"><button className="btn btn-primary" type="submit" disabled={f.busy}>{t("obSaveContinue")}</button></div>
    </form>
  );
}

function BranchSelect({ value, onChange, branches }: { value: string; onChange(v: string): void; branches: BranchRecordDto[] }) {
  const { t, L } = useLang();
  return (
    <div className="field"><label htmlFor="ob-branch" className="req">{t("branch")}</label>
      <select id="ob-branch" className="control" value={value} onChange={(e) => onChange(e.target.value)}>{branches.map((b) => <option key={b.id} value={b.id}>{L(b)}</option>)}</select></div>
  );
}

function WarehouseStep({ ob, onDone }: StepProps) {
  const { t, L } = useLang();
  const s = useSession();
  const rows = useList<WarehouseRecordDto>("/settings/warehouses");
  const branches = useList<BranchRecordDto>("/settings/branches");
  const f = useForm({ nameEn: "", nameAr: "", branchId: "" });
  if (!rows || !branches) return <p>{t("loading")}</p>;
  const branchId = f.v.branchId || branches[0]?.id || "";
  const save = async () => { if (await f.run(() => api("POST", "/settings/warehouses", { nameEn: f.v.nameEn, nameAr: f.v.nameAr, branchId }))) { await s.refresh(); await onDone(); } };
  return ob.steps.warehouse === "done" ? <Existing names={rows.map((r) => L(r))} onDone={onDone} /> : (
    <form noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <p className="hint">{t("obWarehouseHint")}</p>
      <div className="ob-form">{f.input("nameEn", t("nameEn"), { req: true })}{f.input("nameAr", t("nameAr"), { req: true })}
        <BranchSelect value={branchId} branches={branches} onChange={(b) => f.setV((x) => ({ ...x, branchId: b }))} /></div>
      <Alert text={f.alert} />
      <div className="ob-actions"><button className="btn btn-primary" type="submit" disabled={f.busy || !branchId}>{t("obSaveContinue")}</button></div>
    </form>
  );
}

function TreasuryStep({ ob, onDone }: StepProps) {
  const { t, L } = useLang();
  const s = useSession();
  const rows = useList<TreasuryRecordDto>("/settings/treasuries");
  const branches = useList<BranchRecordDto>("/settings/branches");
  const f = useForm({ nameEn: "", nameAr: "", branchId: "", kind: "cash", accountNumber: "", iban: "" });
  if (!rows || !branches) return <p>{t("loading")}</p>;
  const branchId = f.v.branchId || branches[0]?.id || "";
  const bank = f.v.kind === "bank";
  const save = async () => {
    const body = { nameEn: f.v.nameEn, nameAr: f.v.nameAr, branchId, kind: f.v.kind, accountNumber: bank ? f.v.accountNumber : "", iban: bank ? f.v.iban : "" };
    if (await f.run(() => api("POST", "/settings/treasuries", body))) { await s.refresh(); await onDone(); }
  };
  return ob.steps.treasury === "done" ? <Existing names={rows.map((r) => L(r))} onDone={onDone} /> : (
    <form noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <p className="hint">{t("obTreasuryHint")}</p>
      <div className="ob-form">{f.input("nameEn", t("nameEn"), { req: true })}{f.input("nameAr", t("nameAr"), { req: true })}
        <BranchSelect value={branchId} branches={branches} onChange={(b) => f.setV((x) => ({ ...x, branchId: b }))} />
        <div className="field"><span className="label">{t("type")}</span>
          <div className="segmented">{(["cash", "bank"] as const).map((k) => (
            <label key={k}><input type="radio" name="ob-kind" checked={f.v.kind === k} onChange={() => f.setV((x) => ({ ...x, kind: k }))} /><span><Icon name={k} />{t(k === "cash" ? "cash" : "bank")}</span></label>
          ))}</div></div>
        {bank && <>{f.input("accountNumber", t("accountNumber"), { ltr: true })}{f.input("iban", t("ibanF"), { ltr: true })}</>}
      </div>
      <Alert text={f.alert} />
      <div className="ob-actions"><button className="btn btn-primary" type="submit" disabled={f.busy || !branchId}>{t("obSaveContinue")}</button></div>
    </form>
  );
}

function Existing({ names, onDone }: { names: string[]; onDone(): Promise<void> }) {
  const { t } = useLang();
  return (
    <>
      <div className="dlg-alert show good" role="status"><Icon name="check" /><span>{t("obExisting", { x: names.join(t("listSep")) })}</span></div>
      <div className="ob-actions"><button className="btn btn-primary" onClick={() => void onDone()}>{t("obContinue")}</button></div>
    </>
  );
}

function AccountingStep({ ob, onDone }: StepProps) {
  const { t } = useLang();
  const [err, setErr] = useState<string | null>(null);
  const set = async (state: "done" | "later") => {
    setErr(null);
    try { await api("PUT", "/onboarding/steps/accounting", { state }); await onDone(); }
    catch (e) { setErr(e instanceof ApiFailure && e.code === "onboarding_incomplete" ? t("obNeedAccounts") : errorText(t, e)); }
  };
  const c = ob.counts;
  return (
    <>
      <div className="hint-box"><Icon name="info" /><p>{t("obAccountingBody")}</p></div>
      <div className="ob-template" data-testid="ob-template"><Link className="btn btn-primary" to="/settings/chart-template"><Icon name="download" />{t("obInstallTemplate")}</Link><span className="hint">{t("obInstallTemplateHint")}</span></div>
      <p className="hint" data-testid="ob-accounting-counts">{t("obAccountingCounts", { a: c.accounts, t: c.taxTypes, k: c.itemKinds })}</p>
      <div className="ob-links">
        <Link className="btn" to="/settings/accountIndex">{t("obOpenChart")}</Link><Link className="btn" to="/settings/tax">{t("obOpenTax")}</Link><Link className="btn" to="/item-kinds">{t("obOpenItemKinds")}</Link>
      </div>
      <Alert text={err} />
      <div className="ob-actions">
        <button className="btn" onClick={() => void set("later")}>{t("obDoLater")}</button>
        <button className="btn btn-primary" onClick={() => void set("done")}>{t("obMarkDone")}</button>
      </div>
    </>
  );
}

function LogoStep({ ob, onDone, onFinish }: StepProps & { onFinish(): Promise<void> }) {
  const { t } = useLang();
  const toast = useToast();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file: File) => {
    setBusy(true); setErr(null);
    try { await apiUpload("/settings/organization/logo", file); await api("PUT", "/onboarding/steps/logo", { state: "done" }); await onDone(); }
    catch (e) { setErr(errorText(t, e)); } finally { setBusy(false); }
  };
  const later = async () => { await api("PUT", "/onboarding/steps/logo", { state: "later" }); await onDone(); };
  const missing = ONBOARDING_STEPS.filter((k) => ob.steps[k] === "todo");
  const finish = async () => {
    try { await api("POST", "/onboarding/complete"); toast(t("obFinished")); await onFinish(); }
    catch (e) { setErr(e instanceof ApiFailure && e.code === "onboarding_incomplete" ? t("obMissing", { x: e.missing.map((k) => t(`obStep_${k}`)).join(t("listSep")) }) : errorText(t, e)); }
  };
  return (
    <>
      {ob.steps.logo === "done" ? <div className="dlg-alert show good" role="status"><Icon name="check" /><span>{t("obState_done")}</span></div> : (
        <>
          <p className="hint">{t("obLogoHint")}</p>
          <div className="ob-links">
            <label className="btn"><Icon name="upload" />{t("obUpload")}
              <input type="file" accept="image/png,image/jpeg,image/webp" hidden disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} /></label>
            <button className="btn" onClick={() => void later()}>{t("obDoLater")}</button>
          </div>
        </>
      )}
      {missing.length > 0 && <p className="hint" data-testid="ob-missing">{t("obMissing", { x: missing.map((k) => t(`obStep_${k}`)).join(t("listSep")) })}</p>}
      <Alert text={err} />
      <div className="ob-actions"><button className="btn btn-primary" data-testid="ob-finish" disabled={missing.length > 0} onClick={() => void finish()}>{t("obFinish")}</button></div>
    </>
  );
}
